import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, EnvelopeSimple, MapPin, PaperPlaneTilt } from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const questions = [
  ["What marine services can I request?", "You can request barge fleeting, vessel mooring, shifting and handling coordination, or broader marine logistics support. Tell us the job details and we will review the fit."],
  ["Do you consider project-based work?", "Yes. We welcome both recurring operating needs and project-based requests. Timing, scope, and availability are confirmed case by case."],
  ["Can I send a request after business hours?", "Yes. The website request line is open around the clock. An operations contact will review your details and follow up directly."],
  ["What information should I include?", "Share equipment type, requested dates, service needed, operating location, and your best contact information. Additional documentation can be discussed during review."],
];

export const ContactFaq = () => {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    const form = event.currentTarget;
    const payload = Object.fromEntries(new FormData(form));
    try {
      await axios.post(`${API}/inquiries`, payload);
      form.reset();
      toast.success("Request received", { description: "Gulf Coast Fleeting will review your operating needs." });
    } catch (error) {
      toast.error("Request could not be sent", { description: "Please check each field and try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section" data-testid="contact-section">
      <div id="faq" className="faq-panel" data-testid="faq-panel">
        <span className="eyebrow" data-testid="faq-label">04 / Straight answers</span>
        <h2 data-testid="faq-heading">Before you<br />make the call.</h2>
        <Accordion type="single" collapsible className="faq-list" data-testid="faq-accordion">
          {questions.map(([question, answer], index) => (
            <AccordionItem value={`item-${index}`} key={question} data-testid={`faq-item-${index + 1}`}>
              <AccordionTrigger data-testid={`faq-trigger-${index + 1}`}>{question}</AccordionTrigger>
              <AccordionContent data-testid={`faq-content-${index + 1}`}>{answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="contact-details" data-testid="contact-details">
          <a className="contact-detail-card" href="mailto:inquiry@gulfcoastfleeting.com" data-testid="contact-email-link">
            <div className="contact-detail-heading">
              <span className="contact-detail-icon"><EnvelopeSimple size={21} weight="fill" /></span>
              <span>For all inquiries</span>
              <small>01</small>
            </div>
            <strong className="contact-email-address">inquiry@gulfcoastfleeting.com</strong>
          </a>
          <div className="contact-detail-card" data-testid="headquarters-address">
            <div className="contact-detail-heading">
              <span className="contact-detail-icon"><MapPin size={21} weight="fill" /></span>
              <span>Headquarters</span>
              <small>02</small>
            </div>
            <address>210 S. Carancahua<br />Suite 600<br />Corpus Christi, Texas 78401</address>
          </div>
        </div>
        <div className="request-availability" data-testid="request-availability">
          <Clock size={25} weight="fill" />
          <span>Online request line<strong>Open 24 hours</strong></span>
        </div>
      </div>
      <div className="form-panel" data-testid="quote-request-panel">
        <div className="form-heading">
          <span className="eyebrow light" data-testid="quote-form-label">Request service</span>
          <h2 data-testid="quote-form-heading">Tell us what<br />the job requires.</h2>
          <p data-testid="quote-form-description">Send the essentials. We’ll follow up to confirm scope, timing, and availability.</p>
        </div>
        <form onSubmit={handleSubmit} data-testid="quote-request-form">
          <div className="form-row">
            <label>Name<input name="name" minLength="2" required data-testid="quote-name-input" placeholder="Your name" /></label>
            <label>Company<input name="company" minLength="2" required data-testid="quote-company-input" placeholder="Company name" /></label>
          </div>
          <div className="form-row">
            <label>Email<input name="email" type="email" required data-testid="quote-email-input" placeholder="name@company.com" /></label>
            <label>Phone<input name="phone" type="tel" minLength="7" required data-testid="quote-phone-input" placeholder="(361) 000-0000" /></label>
          </div>
          <label>Service needed
            <select name="service" defaultValue="" required data-testid="quote-service-select">
              <option value="" disabled>Select a service</option>
              <option>Barge fleeting</option><option>Vessel mooring</option>
              <option>Shifting &amp; handling</option><option>Marine logistics</option><option>Other</option>
            </select>
          </label>
          <label>Job details
            <textarea name="message" minLength="10" maxLength="2000" required data-testid="quote-message-input" placeholder="Equipment, timing, location, and operating needs..." rows="5" />
          </label>
          <button className="button button-orange submit-button" type="submit" disabled={submitting} data-testid="quote-submit-button">
            {submitting ? "Sending request..." : "Send operations request"}
            {submitting ? null : <PaperPlaneTilt size={19} weight="fill" />}
          </button>
          <p className="form-note" data-testid="quote-privacy-note">By submitting, you agree to be contacted about this request.</p>
        </form>
      </div>
    </section>
  );
};